/* eslint-disable react/no-unescaped-entities */
"use client";

import React, { useState } from "react";

import EnqueryForm from "@/components/forms/EnqueryForm";
import { FormHeading, SuccessMessageType } from "@/interface/types";

const DownArrowIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      d="M12.854 9.35375L8.35403 13.8538C8.30759 13.9002 8.25245 13.9371 8.19175 13.9623C8.13105 13.9874 8.06599 14.0004 8.00028 14.0004C7.93457 14.0004 7.86951 13.9874 7.80881 13.9623C7.74811 13.9371 7.69296 13.9002 7.64653 13.8538L3.14653 9.35375C3.05271 9.25993 3 9.13268 3 9C3 8.86732 3.05271 8.74007 3.14653 8.64625C3.24035 8.55243 3.3676 8.49972 3.50028 8.49972C3.63296 8.49972 3.76021 8.55243 3.85403 8.64625L7.50028 12.2931V2.5C7.50028 2.36739 7.55296 2.24021 7.64672 2.14645C7.74049 2.05268 7.86767 2 8.00028 2C8.13289 2 8.26006 2.05268 8.35383 2.14645C8.4476 2.24021 8.50028 2.36739 8.50028 2.5V12.2931L12.1465 8.64625C12.2403 8.55243 12.3676 8.49972 12.5003 8.49972C12.633 8.49972 12.7602 8.55243 12.854 8.64625C12.9478 8.74007 13.0006 8.86732 13.0006 9C13.0006 9.13268 12.9478 9.25993 12.854 9.35375Z"
      fill="#3399FF"
    />
  </svg>
);

type FaqAnswer =
  | string
  | { intro?: string; list: string[]; outro?: string }
  | {
      intro?: string;
      table: { headers: string[]; rows: string[][] };
      outro?: string;
    };
type FaqItem = { value: string; question: string; answer: FaqAnswer };

const renderRichText = (text?: string): React.ReactNode => {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={i}>{part.slice(2, -2)}</strong>;
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
};

// CPA (Certified Public Accountant) FAQs
const faqsData: FaqItem[] = [
  {
    value: "cpa-faq-1",
    question: "What is the US CPA course?",
    answer:
      "The US CPA (Certified Public Accountant) is the global benchmark qualification for accounting, auditing, taxation, and finance professionals. It is widely regarded as the US equivalent of the Indian CA and is highly valued by Big 4 firms and multinational companies worldwide.",
  },
  {
    value: "cpa-faq-2",
    question: "What is the full form of CPA?",
    answer: "CPA stands for Certified Public Accountant.",
  },
  {
    value: "cpa-faq-3",
    question: "Is the US CPA valid in India?",
    answer:
      "Yes. US CPA professionals are highly in demand in India, especially at Big 4 firms, Global Capability Centers (GCCs), consulting firms, and multinational corporations handling US clients and reporting.",
  },
  {
    value: "cpa-faq-4",
    question: "Is CPA better than CA?",
    answer:
      "Both qualifications are valuable, but they serve different career goals. CA is ideal for Indian statutory practice, while CPA is designed for global accounting, audit, finance, and taxation careers.",
  },
  {
    value: "cpa-faq-5",
    question: "Which is tougher CA or CPA?",
    answer: {
      intro: "Most students find CPA more manageable because:",
      list: [
        "It has only 4 exams",
        "Exams are computer-based",
        "You can take one exam at a time",
        "The course is designed for working professionals",
      ],
    },
  },
  {
    value: "cpa-faq-6",
    question: "How many exams are there in CPA?",
    answer: {
      intro: "The CPA course has 4 exams:",
      list: ["FAR", "AUD", "REG", "BAR/ISC/TCP discipline section"],
    },
  },
  {
    value: "cpa-faq-7",
    question: "What is the duration of the CPA course?",
    answer: "Most students complete the CPA course in 12–16 months.",
  },
  {
    value: "cpa-faq-8",
    question: "Can working professionals do CPA?",
    answer:
      "Yes. The CPA course is specifically designed for working professionals.",
  },
  {
    value: "cpa-faq-9",
    question: "Are CPA exams conducted in India?",
    answer:
      "Yes. CPA exams can be taken from Prometric centers across major Indian cities.",
  },
  {
    value: "cpa-faq-10",
    question: "Is there negative marking in CPA exams?",
    answer: "No. There is no negative marking.",
  },
  {
    value: "cpa-faq-11",
    question: "Who is eligible for the CPA exam?",
    answer: {
      intro:
        "Eligibility depends on US credit requirements set by State Boards. Generally:",
      list: [
        "120 credits are required for exam eligibility",
        "150 credits are required for CPA licensure",
      ],
    },
  },
  {
    value: "cpa-faq-12",
    question: "Can I do CPA after B.Com?",
    answer:
      "Yes. B.Com graduates can pursue CPA through the Miles Bridge Program.",
  },
  {
    value: "cpa-faq-13",
    question: "I only have a 3-year degree. Can I still do CPA?",
    answer:
      "Yes. Miles offers an integrated bridge pathway to help candidates meet credit requirements.",
  },
  {
    value: "cpa-faq-14",
    question: "Can final-year students apply for CPA?",
    answer:
      "Yes. Final-year students can start preparation and become eligible through bridge pathways if needed.",
  },
  {
    value: "cpa-faq-15",
    question: "Can engineers pursue CPA?",
    answer:
      "Yes. B.Tech graduates generally already have 120 credits and are often eligible for the CPA exam.",
  },
  {
    value: "cpa-faq-16",
    question: "What are CPA credits?",
    answer:
      "In the US education system, 1 year of university education = 30 credits.",
  },
  {
    value: "cpa-faq-17",
    question: "Is M.Com required for CPA?",
    answer: "Not always. It depends on your existing educational credits.",
  },
  {
    value: "cpa-faq-18",
    question: "What is the CPA Bridge Program?",
    answer:
      "The Bridge Program helps students with 90 credits become eligible for the CPA exam through an integrated Master’s pathway.",
  },
  {
    value: "cpa-faq-19",
    question: "Can I write CPA while doing M.Com?",
    answer:
      "Yes. Miles allows students to pursue both simultaneously under the pre-education option.",
  },
  {
    value: "cpa-faq-20",
    question: "Is articleship required for CPA?",
    answer: "No. CPA does not require an articleship like CA.",
  },
  {
    value: "cpa-faq-21",
    question: "What subjects are covered in CPA?",
    answer: {
      intro: "The CPA syllabus includes:",
      list: [
        "Financial Accounting",
        "Auditing",
        "Taxation",
        "Business Reporting",
        "Analytics",
        "Regulatory concepts",
      ],
    },
  },
  {
    value: "cpa-faq-22",
    question: "Which CPA exam is the hardest?",
    answer: "FAR is generally considered the most extensive section.",
  },
  {
    value: "cpa-faq-23",
    question: "How many hours should I study for CPA?",
    answer: "Most students study around 15–20 hours per week.",
  },
  {
    value: "cpa-faq-24",
    question: "Are CPA exams MCQ-based?",
    answer: {
      intro: "Yes. CPA exams include:",
      list: [
        "Multiple Choice Questions (MCQs)",
        "Task-Based Simulations (TBS)",
      ],
    },
  },
  {
    value: "cpa-faq-25",
    question: "What is the passing score for CPA?",
    answer: "You need a score of 75 to pass each exam section.",
  },
  {
    value: "cpa-faq-26",
    question: "What is NTS in CPA?",
    answer:
      "NTS stands for Notice to Schedule. It is the official exam authorization document.",
  },
  {
    value: "cpa-faq-27",
    question: "How long are CPA exam credits valid?",
    answer:
      "Candidates generally get 30 months to clear all remaining exams after passing the first section.",
  },
  {
    value: "cpa-faq-28",
    question: "What is CAIRA?",
    answer:
      "CAIRA stands for Certified AI-Ready Accountant. It is Miles Education’s AI-integrated accounting credential designed for accountants, auditors, tax professionals, and finance teams.",
  },
  {
    value: "cpa-faq-29",
    question: "Why is CAIRA integrated with CPA?",
    answer:
      "Modern accounting firms now expect professionals to understand AI workflows, automation, analytics, and AI tools alongside accounting knowledge.",
  },
  {
    value: "cpa-faq-30",
    question: "Will AI replace accountants?",
    answer:
      "AI will not replace accountants. But accountants who use AI will replace those who don’t.",
  },
  {
    value: "cpa-faq-31",
    question: "What AI tools are taught in CAIRA?",
    answer: {
      intro: "Students learn tools such as:",
      list: [
        "Microsoft Copilot",
        "Power BI",
        "Power Automate",
        "Copilot Studio",
        "AI workflow systems",
      ],
    },
  },
  {
    value: "cpa-faq-32",
    question: "What are AI workflows in accounting?",
    answer: {
      intro:
        "AI workflows automate repetitive accounting, audit, and tax processes such as:",
      list: [
        "Audit testing",
        "Variance analysis",
        "Tax drafting",
        "Reporting",
        "Dashboarding",
        "Compliance tracking",
      ],
    },
  },
  {
    value: "cpa-faq-33",
    question: "What is an AI-Ready CPA?",
    answer:
      "An AI-Ready CPA combines accounting expertise with AI execution capability across audit, tax, FP&A, and finance operations.",
  },
  {
    value: "cpa-faq-34",
    question: "Why are Big 4 firms hiring AI-ready accountants?",
    answer:
      "Big 4 firms and MNCs are increasingly adopting AI-powered finance systems and need professionals who can operate and manage these workflows.",
  },
  {
    value: "cpa-faq-35",
    question: "Is CAIRA included with Miles CPA?",
    answer: "Yes. CAIRA Level 1 is included for Miles CPA students.",
  },
  {
    value: "cpa-faq-36",
    question: "What are examples of AI use cases taught in CPA + CAIRA?",
    answer: {
      intro: "Students are exposed to:",
      list: [
        "Revenue Recognition Agents",
        "Audit Analytics Engines",
        "Fraud Detection Systems",
        "Tax Return Drafting Agents",
        "Forecasting & Scenario Intelligence",
        "AI-powered FP&A dashboards",
      ],
    },
  },
  {
    value: "cpa-faq-37",
    question: "Can AI automate auditing?",
    answer:
      "AI can automate repetitive audit procedures, anomaly detection, sampling analysis, and workpaper drafting while auditors focus on judgment and review.",
  },
  {
    value: "cpa-faq-38",
    question: "How is AI used in taxation?",
    answer: {
      intro: "AI tools are increasingly used for:",
      list: [
        "Tax return drafting",
        "Tax research",
        "Compliance workflows",
        "Notice response drafting",
        "Multi-state tax analysis",
      ],
    },
  },
  {
    value: "cpa-faq-39",
    question: "How is AI used in FP&A and finance?",
    answer: {
      intro: "AI helps finance teams with:",
      list: [
        "Forecasting",
        "Variance analysis",
        "Dashboarding",
        "KPI tracking",
        "Continuous close workflows",
        "Financial commentary generation",
      ],
    },
  },
  {
    value: "cpa-faq-40",
    question: "Why is AI important for accountants today?",
    answer:
      "Accounting is moving from manual execution to exception-driven, AI-assisted workflows. Firms now value professionals who can work with automation, analytics, and AI systems.",
  },
  {
    value: "cpa-faq-41",
    question: "What is the salary of a CPA in India?",
    answer:
      "Freshers with CPA qualifications can earn ₹12 LPA+ in India, especially at Big 4 firms and MNCs.",
  },
  {
    value: "cpa-faq-42",
    question: "What jobs can I get after CPA?",
    answer: {
      intro: "Common roles include:",
      list: [
        "Auditor",
        "Tax Consultant",
        "FP&A Analyst",
        "Financial Analyst",
        "Controller",
        "Advisory Associate",
        "Corporate Finance Professional",
      ],
    },
  },
  {
    value: "cpa-faq-43",
    question: "Can I work in the US after CPA?",
    answer:
      "Yes. CPA is highly valued in the US and can open pathways through the Miles US Pathway program.",
  },
  {
    value: "cpa-faq-44",
    question: "Are CPAs in demand globally?",
    answer:
      "Yes. There is a major global shortage of accountants, especially in the US market.",
  },
  {
    value: "cpa-faq-45",
    question: "Do Big 4 firms hire CPAs?",
    answer:
      "Yes. Deloitte, EY, KPMG, PwC, and other global firms actively hire CPAs.",
  },
  {
    value: "cpa-faq-46",
    question: "Why is Miles Education famous for CPA?",
    answer:
      "80%+ CPAs in India are Miles alumni. Miles is one of India’s leading CPA training providers.",
  },
  {
    value: "cpa-faq-47",
    question: "Does Miles provide placement support?",
    answer:
      "Yes. Miles provides placement assistance with Big 4 firms and 220+ MNCs.",
  },
  {
    value: "cpa-faq-48",
    question: "Who is Varun Jain?",
    answer:
      "Varun Jain (CPA, CMA) is the founder of Miles Education and one of the most recognized CPA instructors globally.",
  },
  {
    value: "cpa-faq-49",
    question: "Does Miles offer online CPA classes?",
    answer:
      "Yes. Miles offers live online classes, recorded content, LMS support, and mentorship.",
  },
  {
    value: "cpa-faq-50",
    question: "Does Miles provide face-to-face classes?",
    answer:
      "Yes. Face-to-face workshops are conducted in multiple Indian cities.",
  },
  {
    value: "cpa-faq-51",
    question: "What is the Miles One App?",
    answer: {
      intro:
        "The Miles One App is the learning platform where students access:",
      list: [
        "Concept videos",
        "MCQs",
        "Mock exams",
        "Simulations",
        "Progress tracking",
      ],
    },
  },
  {
    value: "cpa-faq-52",
    question: "Does Miles help with CPA licensing?",
    answer: {
      intro: "Yes. Miles assists students with:",
      list: [
        "Eligibility",
        "Credit evaluation",
        "State board selection",
        "NTS",
        "Exam scheduling",
        "CPA licensing",
      ],
    },
  },
  {
    value: "cpa-faq-53",
    question: "Does Miles support AI learning for accountants?",
    answer:
      "Yes. CAIRA is fully integrated into the Miles CPA journey and focuses on AI workflows, analytics, automations, and AI agents for accounting professionals.",
  },
];

const cpaData: FaqItem[] = [
  {
    value: "cpa-1",
    question: "What is a Certified Public Accountant (CPA)?",
    answer: {
      intro:
        "The **US CPA (Certified Public Accountant)** is the world's most respected accounting qualification and the American equivalent of the Indian CA. Administered by the **AICPA (American Institute of Certified Public Accountants)** and regulated by 55 State Boards of Accountancy, it is the professional gold standard for accounting, auditing, taxation, and finance globally.",
      list: [
        "**Yesterday's accountant:** prepares, checks, reconciles, samples, follows up",
        "**Tomorrow's CPA:** builds AI workflows, leverages AI agents, audits full populations, explains exceptions, owns judgment",
        "The new hiring question at Big 4 firms and MNCs: Can you help us implement AI across audit, tax, and finance workflows? **CPA prep must change too.**",
        "Miles Education, the leading **CPA institute** and the **best institute for CPA in India**, has integrated the US CPA course with **CAIRA (Certified AI-Ready Accountant)** — the only program in the world that combines a globally recognized accounting credential with structured AI workflow training built for accountants",
      ],
    },
  },
  {
    value: "cpa-2",
    question: "What Does CPA Stand For?",
    answer: {
      intro:
        "**CPA stands for Certified Public Accountant.** It is a statutory license issued by U.S. State Boards that authorizes holders to:",
      list: [
        "Sign audit reports",
        "Represent clients before the IRS",
        "Deliver a range of attest services",
        "Sit for the Uniform **CPA exam,** developed by the AICPA and conducted at Prometric test centers worldwide, including in India",
        "Indian candidates appear for the same uniform exam as candidates in New York or Chicago",
      ],
    },
  },
  {
    value: "cpa-3",
    question: "US CPA Exam Details & Structure",
    answer: {
      intro:
        "The **US CPA exam** consists of 4 sections — 3 mandatory core sections and 1 discipline section of your choice. All exams are computer-based and include **50% MCQs and 50% Task-Based Simulations (TBS)**. Here are the complete **US CPA exam details** and **AICPA course details**:",
      list: [
        "**FAR - Financial Accounting & Reporting**: US GAAP, IFRS, leases, consolidations, governmental and non-profit accounting. The most content-heavy section. CAIRA use cases: revenue recognition agent, consolidation workflows, SEC reporting copilot, M&A accounting agent, income tax provision copilot (ASC 740)",
        "**AUD - Auditing & Attestation**: audit standards (GAAS & PCAOB), internal controls, risk assessment, audit evidence, professional ethics. CAIRA use cases: full-population testing engine, fraud anomaly detection, audit planning copilot, auto-generated workpapers, going concern intelligence agent",
        "**REG - Taxation & Regulation**: US federal income tax for individuals, corporations, partnerships, estates, and trusts, plus business law and ethics. CAIRA use cases: tax return drafting agent (1040 & 1120), compliance check workflows, notice response copilot, international tax planning agent (GILTI, BEAT, FDII)",
        "**BAR - Business Analysis & Reporting** (most popular discipline globally): financial analysis, forecasting, variance analysis, business reporting. CAIRA use cases: forecasting & scenario agent, variance commentary copilot, KPI scorecard platform, working capital monitor",
        "Alternative discipline sections: ISC (Information Systems & Controls) and TCP (Tax Compliance & Planning)",
        "**30 months** to pass the remaining three sections after passing your first — **study for one exam at a time**",
        "Exams are available **365 days a year**; you choose your exam date",
        "**Important for Indian candidates:** For CPA exams taken at international locations (India, Dubai), exam credits lapse unless the candidate achieves the full CPA license within 3 years. Miles' Gameplan Team tracks this timeline actively for every enrolled student",
      ],
    },
  },
  {
    value: "cpa-4",
    question: "US CPA Exam Eligibility",
    answer: {
      intro:
        "**US CPA exam eligibility** is governed by 55 State Boards of Accountancy. The general framework:",
      list: [
        "**120 credit hours** to sit the CPA exam",
        "**150 credit hours** to receive the CPA license",
        "**1 year of relevant work experience** (most states), verified by a licensed CPA",
        "**Ethics exam** completion",
        "Every year of Indian university education = 30 credit hours",
        "A 3-year B.Com = 90 credits (needs the bridge program)",
        "B.Com + M.Com / MBA / CA / CS / CMA = 120–150 credits (fully eligible)",
        "**Eligible backgrounds:** B.Com · M.Com · MBA (Finance) · CA / CS / CMA · BBA · B.Sc (Accounting) · Engineering + Finance · working professionals in accounting and finance",
      ],
    },
  },
  {
    value: "cpa-5",
    question: "The CPA Bridge Program for B.Com Graduates",
    answer: {
      intro:
        "If you hold only a 3-year B.Com (90 credits), **CPA enrollment** is still completely straightforward. Miles offers an approved **Online M.Com Professional Accounting & Finance from Jain University** as a CPA bridge:",
      list: [
        "**No waiting for immediate exam eligibility:** You can start appearing for the CPA exam as soon as you receive your M.Com admit card (pre-education option)",
        "**Zero extra study load:** The M.Com syllabus aligns with CPA coursework, so you can pass your M.Com alongside your CPA. Two outcomes, one effort",
      ],
    },
  },
  {
    value: "cpa-6",
    question: "The 4 Es: How to Get Your CPA License",
    answer: {
      intro:
        "Passing the **CPA exam** alone does not make you a CPA. To receive the full license, you must satisfy all 4 Es:",
      list: [
        "**Education** — 150 credit hours from an accredited institution. Miles assists with the bridge program if required",
        "**Examination** — pass all 4 sections of the Uniform CPA Exam",
        "**Experience** — typically 1 year of relevant accounting/finance work, verified by a licensed CPA. Miles has full-time licensed CPAs on staff (including Lead Instructor Varun Jain, CPA) authorized to verify candidate experience — a critical advantage for Indian candidates that no other CPA training provider in India offers",
        "**Ethics** — an online on-demand ethics course required by most State Boards after passing the exam",
        "Miles is the **only CPA review course provider in India** with licensed CPAs authorized to verify work experience, a make-or-break requirement for getting the CPA license from India",
      ],
    },
  },
  {
    value: "cpa-7",
    question: "CPA vs CA: What's the Difference?",
    answer: {
      table: {
        headers: ["", "**CA**", "**CPA**"],
        rows: [
          ["**Authority**", "ICAI (India)", "AICPA + 55 U.S. State Boards"],
          ["**Structure**", "3 levels, 16 exams", "4 exams only"],
          ["**Timeline**", "4–6 years", "12–16 months"],
          ["**Entry**", "After Class XII", "After graduation"],
          [
            "**Focus**",
            "Ind AS, Indian taxation, statutory audit",
            "US GAAP, US taxation, PCAOB audit",
          ],
          [
            "**Best for**",
            "Indian CA firms, domestic roles",
            "Big 4, MNCs, GCCs, U.S.-facing roles",
          ],
          ["**AI curriculum**", "None", "Integrated with CAIRA"],
        ],
      },
      outro:
        "If your goal is to work with Big 4 firms, a GCC, or an MNC in India or the United States, the CPA is the right qualification. Big 4 firms in India hire CPAs specifically because their engagements are built around US GAAP and US audit standards.",
    },
  },
  {
    value: "cpa-8",
    question: "CPA Course Duration & Format",
    answer: {
      intro:
        "The **CPA course** is designed for working professionals. Full **US CPA course details**:",
      list: [
        "4 exam sections",
        "12–16 months to complete",
        "365 exam days per year",
        "18–20 study hours per week",
        "50% MCQs + 50% Task-Based Simulations",
        "100% computer-graded",
        "30-month window after passing the first section",
        "Study one exam at a time",
      ],
    },
  },
  {
    value: "cpa-9",
    question: "CPA Course Fees in India",
    answer: {
      intro:
        "Full **CPA course fees in India** at Miles Education, including all **CPA registration fees**. **Miles Program Fees:**",
      table: {
        headers: ["**Component**", "**Fee**"],
        rows: [
          ["CPA Study Materials", "₹49,000"],
          ["CPA Support Fees", "₹10,000"],
          ["CPA Training Fees", "₹40,000"],
          ["**CPA Total**", "**₹99,000**"],
          ["CAIRA (discounted bundle)", "₹50,000"],
          ["**CPA + CAIRA Bundle**", "**₹1,49,000**"],
        ],
      },
      outro:
        "EMI: ₹9,917/month over 12 months, zero interest, zero processing fees (₹30,000 upfront). **Additional CPA exam fees** payable to U.S. State Board: ~**$873 per exam section** (application ~$100 + section ~$263 + international testing ~$510). Foreign Academic Credential Evaluation: ~**$400 one-time**.",
    },
  },
  {
    value: "cpa-10",
    question: "CPA + CAIRA: The AI-Ready Accountant",
    answer: {
      intro:
        "The firms hiring right now want accountants who can **build, automate, and govern,** not just prepare and check. Miles integrated CAIRA as a parallel AI curriculum that runs alongside every CPA exam section.",
      list: [
        "**CPA gives you professional credibility:** globally recognized credential · US GAAP, audit & tax expertise · pathways to Big 4, MNCs, GCCs, and public accounting",
        "**CAIRA gives you the AI edge:** Microsoft 365 Copilot · Power BI · Power Automate · Copilot Studio",
        "3 Levels · 90 Hours · NASBA-approved CPE · CAIRA Level 1 badge on LinkedIn within 4 months",
        "**CAIRA Level 1:** AI Foundations — master the Microsoft AI stack (Prompts → Analytics → Automations → Agents)",
        "**CAIRA Level 2:** Applied AI — AI-first shift across audit, tax, close, and FP&A",
        "**CAIRA Level 3:** AI Leadership — firm-wide AI operating model, agents at scale, governance and trust",
      ],
    },
  },
  {
    value: "cpa-11",
    question: "US CPA Jobs in India: Career & Salary",
    answer: {
      intro:
        "**CPA accountant** professionals are among the most in-demand in global finance right now. **US CPA jobs in India** span Big 4 offices, GCCs, MNC finance centers, and U.S.-headquartered accounting firms.",
      list: [
        "**Average CPA course salary in India:** ₹20 lakhs+",
        "**Average CPA job salary in the U.S.:** ₹50 lakhs+ (~$60,000–$120,000 USD)",
        "**MNCs actively hiring Miles CPAs:** 220+",
        "**Common CPA job titles:** Audit Associate · Tax Consultant · Financial Analyst · FP&A Manager · Controller · Finance Business Partner · Advisory Associate · Risk Consultant · Corporate Accountant",
        "**Hiring firms:** Deloitte · EY · KPMG · PwC · BDO · RSM · Grant Thornton · Morgan Stanley · JP Morgan · Amazon · Accenture · Genpact · J&J · Wipro · HP · Barclays · PepsiCo · Philips · and 200+ more",
        "**CPA job functions:** Audit & Assurance · US Taxation · FP&A · Financial Reporting · Corporate Finance · Advisory · Risk & Controls · Controllership · M&A · GCC Finance",
      ],
    },
  },
  {
    value: "cpa-12",
    question: "Why Miles Is the Best CPA Institute in India",
    answer: {
      intro:
        "Miles Education is India's **best CPA coaching** provider. **80%+ of all CPAs in India are Miles alumni.** Our Miles pass rate of **~82%** versus the global average of **~47%** is the clearest proof of what exceptional **CPA coaching** looks like. **Searching for CPA coaching online or a CPA institute near me?** Miles runs **CPA classes** across 9 cities — Bangalore · Hyderabad · Mumbai · Delhi · Pune · Ahmedabad · Chennai · Kochi · Kolkata — and full **CPA classes online** through the Miles One App. **What Miles students get:**",
      list: [
        "100+ hours of concept video learning with Varun Jain, world's leading CPA instructor",
        "E-books (hard copies published by McGraw Hill)",
        "8,000+ MCQs with detailed explanations for all 4 sections",
        "500+ Task-Based Simulations in CPA exam interface",
        "Mock exams and 4-hour full-length practice exams",
        "Live online sessions plus weekend CPA classes across 9 cities",
        "1:1 mentorship (bookable slots on the LMS)",
        "Complete **CPA enrollment** guidance, state board selection, and credential evaluation",
        "CPA license support — experience verification by Miles' licensed CPAs (unique to Miles)",
        "Placement assistance with Big 4 and 220+ MNCs",
        "CAIRA AI masterclasses: Microsoft Copilot, Power Automate, Power BI for Finance, Build AI Agents with Copilot Studio",
      ],
    },
  },
];

// Miles data
const milesData: FaqItem[] = [
  {
    value: "miles-1",
    question: "About Miles Education",
    answer:
      "The global talent landscape has critical gaps, but at Miles Education, we're not just filling these voids; we're building pathways to success. Driven by our mission to work backward from industry shortages, skill gaps, and opportunities, we empower businesses and enable talent by combining academic learning with hands-on experience through our unique ED-WORK Pedagogy.",
  },
];

const NewFaqSection = ({ pageName }: { pageName: string }) => {
  const [activeTab, setActiveTab] = useState("FAQs");
  const [showAllFaqs, setShowAllFaqs] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleExpand = (value: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case "FAQs":
        return faqsData;
      case "CPA":
        return cpaData;
      case "Miles":
        return milesData;
      default:
        return faqsData;
    }
  };

  const visibleItems = showAllFaqs
    ? getCurrentData()
    : getCurrentData().slice(0, 3);

  const TAB_GRADIENT =
    "radial-gradient(1474.29% 161.47% at 113.4% 210.95%, #b9dcff 0%, #3a9cff 100%)";

  // Tailwind arbitrary variants for rich-text styles inside accordion answers
  const accordionRichText =
    // .answer
    "[&_.answer]:text-sm [&_.answer]:font-normal [&_.answer]:leading-5 " +
    // .faqList
    "[&_.faqList]:list-disc [&_.faqList]:ml-5 [&_.faqList]:text-sm [&_.faqList]:font-normal [&_.faqList]:my-1 " +
    // .faqTableWrapper
    "[&_.faqTableWrapper]:overflow-x-auto [&_.faqTableWrapper]:mt-3 [&_.faqTableWrapper]:mb-2 [&_.faqTableWrapper]:border [&_.faqTableWrapper]:border-[#E5EAF2] " +
    // .faqTable
    "[&_.faqTable]:w-full [&_.faqTable]:border-collapse [&_.faqTable]:text-[0.8rem] [&_.faqTable]:bg-white " +
    // .faqTable thead
    "[&_.faqTable_thead]:bg-[#EAF2FC] " +
    // .faqTable th & td
    "[&_.faqTable_th]:px-4 [&_.faqTable_th]:py-3 [&_.faqTable_th]:text-left [&_.faqTable_th]:text-[#1d1d1d] [&_.faqTable_th]:font-semibold [&_.faqTable_th]:align-top " +
    "[&_.faqTable_td]:px-4 [&_.faqTable_td]:py-3 [&_.faqTable_td]:text-left [&_.faqTable_td]:text-[#1d1d1d] [&_.faqTable_td]:align-top " +
    // zebra striping
    "[&_.faqTable_tbody_tr:nth-child(even)]:bg-[#F7FBFF] " +
    // first column muted label
    "[&_.faqTable_tbody_tr_td:first-child]:!text-[#94A3B8] [&_.faqTable_tbody_tr_td:first-child]:font-medium " +
    // .about-section-content
    "[&_.about-section-content]:text-sm [&_.about-section-content]:leading-[1.55] [&_.about-section-content]:text-[#333] " +
    "[&_.about-section-content_p]:mt-0 [&_.about-section-content_p]:mb-3 " +
    // .about-section-list
    "[&_.about-section-list]:list-disc [&_.about-section-list]:mt-2 [&_.about-section-list]:mb-3 [&_.about-section-list]:ml-5 [&_.about-section-list]:text-sm " +
    "[&_.about-section-list_li]:mb-[0.35rem]";

  return (
    <section className="py-8 md:pt-12 md:pb-16 md:bg-[#FFFF]">
      <div className="main_container w-full px-4 md:max-w-[1280px] md:mx-auto md:px-6">
        <div className="flex flex-col gap-6 min-[1200px]:flex-row min-[1200px]:gap-8 min-[1200px]:items-stretch">
          {/* Form column — order 1 mobile, order 2 desktop */}
          <div
            id="live-webinar-section"
            className="w-full order-1 md:order-2 md:w-[380px] md:flex-[0_0_380px]"
          >
            <div
              id="live-webinar-form"
              className="rounded-2xl bg-white border border-[#DEE7F5] p-5"
              style={{
                transition: "all 0.5s ease-in-out",
                position: "sticky",
                top: 100,
              }}
            >
              <EnqueryForm
                pageName={pageName}
                courseId={pageName === "CMA" ? 2 : 1}
                darkBackground={false}
                submitButtonName="Request a Callback"
                formType="Enquiry Form"
                isAStepperForm
                successMessage={SuccessMessageType.ACCOUNTING}
                formHeading={FormHeading.TALK_TO_AN_EXPERT}
              />
            </div>
          </div>

          {/* Content column — order 2 mobile, order 1 desktop */}
          <div className="w-full order-2 md:order-1 md:w-[833px] md:flex-[0_0_833px]">
            <div className="bg-white rounded-2xl py-6 md:px-0 px-0">
              <div className="flex flex-col justify-center md:flex-row md:gap-3 md:items-center md:justify-center">
                <h2 className="text-2xl font-bold text-center mb-4 text-[#333] md:text-[2rem] md:mb-0">
                  All About
                </h2>

                <div className="flex p-1 items-center rounded-2xl bg-[#f3f3f3] mb-6 w-full md:w-[337px] md:mb-0 md:flex-none">
                  <button
                    className={`flex p-2 justify-center items-center gap-2.5 flex-1 rounded-2xl border-0 text-base font-semibold cursor-pointer transition-all duration-300 md:text-lg ${
                      activeTab === "CPA"
                        ? "text-white"
                        : "bg-transparent text-[#1d1d1d]"
                    }`}
                    style={
                      activeTab === "CPA"
                        ? { background: TAB_GRADIENT }
                        : undefined
                    }
                    onClick={() => setActiveTab("CPA")}
                  >
                    CPA
                  </button>
                  <button
                    className={`flex p-2 justify-center items-center gap-2.5 flex-1 rounded-2xl border-0 text-base font-semibold cursor-pointer transition-all duration-300 md:text-lg ${
                      activeTab === "Miles"
                        ? "text-white"
                        : "bg-transparent text-[#1d1d1d]"
                    }`}
                    style={
                      activeTab === "Miles"
                        ? { background: TAB_GRADIENT }
                        : undefined
                    }
                    onClick={() => setActiveTab("Miles")}
                  >
                    Miles
                  </button>
                  <button
                    className={`flex p-2 justify-center items-center gap-2.5 flex-1 rounded-2xl border-0 text-base font-semibold cursor-pointer transition-all duration-300 md:text-lg ${
                      activeTab === "FAQs"
                        ? "text-white"
                        : "bg-transparent text-[#1d1d1d]"
                    }`}
                    style={
                      activeTab === "FAQs"
                        ? { background: TAB_GRADIENT }
                        : undefined
                    }
                    onClick={() => setActiveTab("FAQs")}
                  >
                    FAQs
                  </button>
                </div>
              </div>

              <div className="mt-4">
                {visibleItems.map((item) => {
                  const enableReadMore = activeTab === "CPA";
                  const isExpanded = expandedItems.has(item.value);
                  const isCollapsed = enableReadMore && !isExpanded;
                  return (
                    <div
                      key={item.value}
                      className={`flex flex-col gap-2.5 p-4 rounded-2xl bg-[#F7FBFF] mb-4 border-0 ${accordionRichText}`}
                    >
                      <div className="text-sm font-bold text-[#333] p-0 w-full text-left leading-normal md:text-base">
                        {item.question}
                      </div>
                      <div
                        className={`relative p-0 text-xs font-normal leading-normal text-[#999] md:text-sm ${
                          isCollapsed ? "max-h-[120px] overflow-hidden" : ""
                        }`}
                      >
                        {typeof item.answer === "string" ? (
                          renderRichText(item.answer)
                        ) : "table" in item.answer ? (
                          <>
                            {item.answer.intro && (
                              <p className="answer">
                                {renderRichText(item.answer.intro)}
                              </p>
                            )}
                            <div className="faqTableWrapper">
                              <table className="faqTable">
                                <thead>
                                  <tr>
                                    {item.answer.table.headers.map((h, i) => (
                                      <th key={i}>{renderRichText(h)}</th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {item.answer.table.rows.map((row, ri) => (
                                    <tr key={ri}>
                                      {row.map((cell, ci) => (
                                        <td key={ci}>{renderRichText(cell)}</td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            {item.answer.outro && (
                              <p className="answer">
                                {renderRichText(item.answer.outro)}
                              </p>
                            )}
                          </>
                        ) : (
                          <>
                            {item.answer.intro && (
                              <p className="answer">
                                {renderRichText(item.answer.intro)}
                              </p>
                            )}
                            <ul className="faqList">
                              {item.answer.list.map((li, idx) => (
                                <li key={idx}>{renderRichText(li)}</li>
                              ))}
                            </ul>
                            {item.answer.outro && (
                              <p className="answer">
                                {renderRichText(item.answer.outro)}
                              </p>
                            )}
                          </>
                        )}
                        {isCollapsed && (
                          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-[#F7FBFF] to-transparent" />
                        )}
                      </div>
                      {enableReadMore && (
                        <button
                          type="button"
                          onClick={() => toggleExpand(item.value)}
                          className="self-start mt-1 inline-flex items-center bg-transparent text-[#39f] border-0 text-xs md:text-sm font-bold cursor-pointer p-0"
                        >
                          {isExpanded ? "Read Less" : "Read More"}
                        </button>
                      )}
                    </div>
                  );
                })}

                {getCurrentData().length > 3 && (
                  <button
                    className="flex items-center justify-center gap-1 mt-4 mx-auto p-0 bg-transparent text-[#39f] border-0 text-sm font-bold cursor-pointer w-full"
                    onClick={() => setShowAllFaqs(!showAllFaqs)}
                  >
                    {showAllFaqs ? "See Less" : "See More"}
                    <span
                      className="inline-flex transition-transform duration-200"
                      style={{
                        transform: showAllFaqs ? "rotate(180deg)" : "none",
                      }}
                    >
                      <DownArrowIcon />
                    </span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NewFaqSection;
